'use strict'

import React, { useState } from 'react'
import { createPortal } from 'react-dom'

const LIST_FILTER_KEYS = ['major', 'middle', 'dashboard', 'graph']

/** 行フィルタドロップダウンの固定幅(px)。リスト系で統一する。 */
const DROPDOWN_WIDTH = 200
/** グラフフィルタだけ少し広めの固定幅(px)。 */
const GRAPH_DROPDOWN_WIDTH = 240

/**
 * 行フィルタドロップダウン（ポータルで表示）
 *
 * Excel のフィルタを踏襲する。検索ボックス・全選択・各項目のチェックは「下書き(staged)」として ローカル state に保持し、OK ボタンまたは検索ボックスでの Enter を押したときだけ store へ反映する。
 * キャンセル／外側クリック／スクロールで閉じた場合は下書きを破棄する（開いた時点の選択に戻る）。
 *
 * 親(MatrixTable)は `key={openRowFilter}` で本コンポーネントを開くたびに再マウントするため、 下書きの初期化は useState の初期化関数で行う（マウント時の currentRowFilters
 * を取り込む）。
 *
 * @param {string} openRowFilter - 開いているフィルタの種類 ('major' | 'middle' | 'dashboard' | 'graph' | 'referable')
 * @param {{ left: number; top: number; width: number }} dropdownRect - ドロップダウンの位置情報
 * @param {Object} rowFilterOptions - フィルタオプション
 * @param {string[]} rowFilterOptions.major - 大分類オプション
 * @param {string[]} rowFilterOptions.middle - 中分類オプション
 * @param {string[]} rowFilterOptions.dashboard - ダッシュボードオプション
 * @param {string[]} rowFilterOptions.graph - グラフオプション
 * @param {Object} currentRowFilters - 現在のフィルタ値（マウント時に下書きへ取り込む）
 * @param {Function} onFilterChange - フィルタ確定ハンドラ（OK/Enter 時のみ呼ぶ）
 * @param {Function} onClose - 閉じるハンドラ（キャンセル時は確定せずに閉じる）
 */
function RowFilterDropdown({
  openRowFilter,
  dropdownRect,
  rowFilterOptions,
  currentRowFilters,
  onFilterChange,
  onClose
}) {
  const isListFilter = LIST_FILTER_KEYS.includes(openRowFilter)

  // 検索テキストと下書き選択(staged)。OK/Enter まで store へは反映しない。
  const [searchText, setSearchText] = useState('')
  const [draftSelected, setDraftSelected] = useState(() =>
    isListFilter ? new Set(currentRowFilters[openRowFilter] || []) : new Set()
  )

  if (!openRowFilter || !dropdownRect) return null

  // グラフは少し広め(240px)、他のリスト系(大分類・中分類・ダッシュボード)は 200px、参照は従来どおりボタン幅。
  const dropdownWidth =
    openRowFilter === 'graph' ? GRAPH_DROPDOWN_WIDTH : isListFilter ? DROPDOWN_WIDTH : dropdownRect.width

  const renderListFilterContent = () => {
    const opt = rowFilterOptions[openRowFilter]
    const searchLower = searchText.trim().toLowerCase()
    const filteredOptions = searchLower ? opt.filter((v) => v.toLowerCase().includes(searchLower)) : opt
    const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every((v) => draftSelected.has(v))
    const someFilteredSelected = !allFilteredSelected && filteredOptions.some((v) => draftSelected.has(v))

    /** 1 項目のチェックを下書きでトグルする */
    const toggleItem = (v) => {
      setDraftSelected((prev) => {
        const next = new Set(prev)
        if (next.has(v)) next.delete(v)
        else next.add(v)
        return next
      })
    }

    /** 表示中(検索で絞った)項目をまとめて選択／解除する */
    const toggleSelectAll = () => {
      setDraftSelected((prev) => {
        const next = new Set(prev)
        if (allFilteredSelected) filteredOptions.forEach((v) => next.delete(v))
        else filteredOptions.forEach((v) => next.add(v))
        return next
      })
    }

    /** OK / Enter で下書きを確定する。 検索中(Excel 準拠の置換): 表示中(マッチ)かつ選択中の項目だけを残し、検索外の選択は捨てる。 未検索時: 下書き全体を（オプションの並び順で）確定する。 */
    const applyFilter = () => {
      const applied = searchLower
        ? filteredOptions.filter((v) => draftSelected.has(v))
        : opt.filter((v) => draftSelected.has(v))
      onFilterChange({ rowFilters: { ...currentRowFilters, [openRowFilter]: applied } })
      onClose()
    }

    return (
      <>
        <div className="p-1.5 border-b border-gray-200 flex-shrink-0 form-control">
          <input
            type="text"
            placeholder="検索"
            className="text-input w-full text-xs text-left"
            value={searchText}
            autoFocus
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyFilter()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                onClose()
              }
            }}
          />
        </div>
        <ul className="py-1 list-none overflow-y-auto flex-1">
          <li
            role="button"
            tabIndex={0}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-[rgba(49,80,165,0.07)] hover:text-[#297ad8] cursor-pointer border-b border-gray-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleSelectAll()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleSelectAll()
              }
            }}
          >
            <input
              type="checkbox"
              checked={allFilteredSelected}
              ref={(el) => {
                if (el) el.indeterminate = someFilteredSelected
              }}
              readOnly
              tabIndex={-1}
              className="rounded border-gray-400 pointer-events-none flex-shrink-0"
            />
            <span className="font-medium">全選択</span>
          </li>
          {filteredOptions.map((v) => {
            const checked = draftSelected.has(v)
            return (
              <li
                key={v}
                role="button"
                tabIndex={0}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-[rgba(49,80,165,0.07)] hover:text-[#297ad8] cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleItem(v)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleItem(v)
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly
                  tabIndex={-1}
                  className="rounded border-gray-400 pointer-events-none flex-shrink-0"
                />
                <span className="truncate min-w-0" title={v}>
                  {v}
                </span>
              </li>
            )
          })}
        </ul>
        <div className="p-1.5 border-t border-gray-200 flex-shrink-0 flex items-center justify-end gap-2">
          <button
            type="button"
            className="text-xs px-3 py-1 rounded border border-gray-400 bg-white text-gray-700 whitespace-nowrap hover:bg-gray-50"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="text-xs px-4 py-1 rounded bg-[#383947] text-white whitespace-nowrap hover:bg-[rgba(56,57,71,0.7)]"
            onClick={(e) => {
              e.stopPropagation()
              applyFilter()
            }}
          >
            OK
          </button>
        </div>
      </>
    )
  }

  const renderReferableContent = () => {
    const referableOptions = [
      { value: 'all', label: 'すべて' },
      { value: 'referable', label: '可' },
      { value: 'unreferable', label: '不可' }
    ]
    return (
      <ul className="year list-none text-caption-2 text-left py-1">
        {referableOptions.map(({ value, label }) => (
          <li
            key={value}
            role="button"
            tabIndex={0}
            className="item text-h7 px-3 py-2 hover:bg-[rgba(49,80,165,0.07)] hover:text-[#297ad8] cursor-pointer text-left"
            onClick={() => {
              onFilterChange({
                rowFilters: { ...currentRowFilters, referable: value }
              })
              onClose()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onFilterChange({
                  rowFilters: { ...currentRowFilters, referable: value }
                })
                onClose()
              }
            }}
          >
            {label}
          </li>
        ))}
      </ul>
    )
  }

  return createPortal(
    <div
      data-row-filter={openRowFilter}
      className="rounded-xl border border-[rgba(116,118,166,0.3)] bg-white text-xs matrix-row-filter-dropdown"
      style={{
        left: dropdownRect.left,
        top: dropdownRect.top,
        width: dropdownWidth,
        maxWidth: dropdownWidth
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {isListFilter ? renderListFilterContent() : openRowFilter === 'referable' ? renderReferableContent() : null}
    </div>,
    document.body
  )
}

export default RowFilterDropdown
